import { useQueryClient } from '@tanstack/react-query';
import { defaultStringifySearch, useRouter, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type { AssignMailInput, UnassignedMail } from '@nest-recruit/shared';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { INBOX_KEY, TIMELINE_KEY, communicationApi, useInbox, useInboxMail } from '../api/communication-api';
import { FilingPanel } from '../components/admin/FilingPanel';
import { MailReader } from '../components/admin/MailReader';
import { MailboxLine } from '../components/admin/MailboxLine';
import { WaitingList } from '../components/admin/WaitingList';
import { shownMail, useInboxActions } from '../components/admin/use-inbox-actions';
import styles from './InboxPage.module.css';

const PATH = '/admin/inbox';
/** semantic.css's laptop and tablet widths: the filing pane folds under the email, then list and email take turns. */
const TWO_MEDIA = '(max-width: 1180px)';
const ONE_MEDIA = '(max-width: 900px)';

interface InboxSearch {
  readonly open?: string;
  readonly page?: number;
}

export function readInboxSearch(raw: Readonly<Record<string, unknown>>): InboxSearch {
  const page = typeof raw.page === 'number' ? raw.page : typeof raw.page === 'string' ? Number(raw.page) : NaN;
  return {
    ...(typeof raw.open === 'string' && raw.open !== '' ? { open: raw.open } : {}),
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
  };
}

/** The email to open once this one leaves the list: the next one down, else the one above. */
export function nextAfter(mail: readonly UnassignedMail[], id: string): string | null {
  const at = mail.findIndex((each) => each.messageId === id);
  if (at < 0) return mail[0]?.messageId ?? null;
  return mail[at + 1]?.messageId ?? mail[at - 1]?.messageId ?? null;
}

/**
 * Inbound mail (F15.04's unassigned queue; Admin redesign batch 2, Option A "triage like an email app"): replies file
 * themselves against their application; mail nobody could be matched to waits here until it is filed against a person
 * or one of their applications, or dismissed (with Undo). After either, the next email opens by itself. Admin only.
 */
export function InboxPage(): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const router = useRouter();
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const search = readInboxSearch(useSearch({ strict: false }) as Readonly<Record<string, unknown>>);
  const page = search.page ?? 1;
  const isTwo = useMediaQuery(TWO_MEDIA);
  const isOne = useMediaQuery(ONE_MEDIA);
  const [isChecking, setIsChecking] = useState(false);

  const go = (next: InboxSearch): void => {
    router.history.replace(`${PATH}${defaultStringifySearch({ ...next })}`);
  };
  const actions = useInboxActions((id) => go({ ...search, open: id }));
  // Nothing is asked of the server for someone who may not open the page.
  const inbox = useInbox(page, canEdit);
  const detail = useInboxMail(canEdit ? (search.open ?? null) : null);

  const view = inbox.data;
  const mail = view ? shownMail(view.mail, actions) : undefined;
  const first = mail?.[0]?.messageId;
  // A wide screen always has an email open, as the drawing has; a phone starts from the list.
  useEffect(() => {
    if (!isOne && search.open === undefined && first) router.history.replace(`${PATH}${defaultStringifySearch({ ...search, open: first })}`);
  }, [isOne, search, first, router]);

  if (!canEdit) return <ForbiddenState resource="the inbound mailbox" contact="Your admin" />;
  if (inbox.error && !view) return <ServerErrorState requestId={inbox.error.requestId} onRetry={() => void inbox.refetch()} />;

  const total = view ? Math.max(0, view.total - (view.mail.length - (mail?.length ?? 0))) : 0;
  const opened = (search.open ? mail?.find((each) => each.messageId === search.open) : undefined) ?? (detail.data?.state === 'waiting' ? detail.data : undefined);
  const isGone = search.open !== undefined && detail.data !== undefined && detail.data.state !== 'waiting' && !opened;

  const check = async (): Promise<void> => {
    setIsChecking(true);
    try {
      const result = await communicationApi.checkInbox();
      await Promise.all([queryClient.invalidateQueries({ queryKey: INBOX_KEY }), queryClient.invalidateQueries({ queryKey: TIMELINE_KEY })]);
      show({ tone: 'success', message: 'Mailbox checked', detail: result.summary });
    } catch (cause) {
      show({ tone: 'danger', message: "The mailbox couldn't be read", detail: errorMessage(cause) });
    } finally {
      setIsChecking(false);
    }
  };
  const leave = (mailItem: UnassignedMail): void => {
    go({ ...search, open: mail ? (nextAfter(mail, mailItem.messageId) ?? undefined) : undefined });
  };
  const file = (mailItem: UnassignedMail, target: AssignMailInput, personName: string): void => {
    leave(mailItem);
    actions.file(mailItem, target, personName);
  };
  const dismiss = (mailItem: UnassignedMail): void => {
    leave(mailItem);
    actions.dismiss(mailItem);
  };

  const showList = !isOne || search.open === undefined;
  const showMail = !isOne || search.open !== undefined;

  const reader = opened ? (
    <>
      <MailReader key={opened.messageId} mail={opened} detail={detail.data?.messageId === opened.messageId ? detail.data : undefined} error={detail.error} onRetry={() => void detail.refetch()} {...(isOne ? { onBack: () => go({ ...search, open: undefined }) } : {})} />
      <FilingPanel key={`file-${opened.messageId}`} mailId={opened.messageId} sender={opened.from} canEdit={canEdit} onFile={(target, name) => file(opened, target, name)} onDismiss={() => dismiss(opened)} />
    </>
  ) : isGone ? (
    <div className={styles.gone}>
      <EmptyState isInset title={detail.data?.state === 'filed' ? 'This email has been filed' : 'This email was dismissed'} body="It isn't waiting any more. Choose another from the list." action={null} />
    </div>
  ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <span className={styles.crumb}>Admin · Messages</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Inbound mail</h1>
          {view && <span className={styles.total}>{formatNumber(total)}</span>}
        </div>
        <p className={styles.lead}>Replies to our emails are filed against their application by themselves. Mail nobody could be matched to waits here until you file it against a person or dismiss it.</p>
      </header>

      <MailboxLine status={view?.status} canCheck={view?.status.isConfigured === true} isChecking={isChecking} onCheck={() => void check()} />

      {mail && mail.length === 0 && !search.open ? (
        <div className={styles.calm}>
          <EmptyState isInset title="Nothing waiting" body="Every reply found its application. New mail that can't be matched appears here by itself." action={null} />
        </div>
      ) : (
        <div className={styles.layout} data-layout={isOne ? 'one' : isTwo ? 'two' : 'three'}>
          {showList && (
            <WaitingList mail={mail} total={total} page={view?.page ?? page} pageSize={view?.pageSize ?? 1} openId={search.open ?? null} onOpen={(id) => go({ ...search, open: id })} onPage={(next) => go({ page: next > 1 ? next : undefined })} />
          )}
          {showMail && reader && <div className={styles.work}>{reader}</div>}
        </div>
      )}
    </div>
  );
}
